import { DOCUMENT } from '@angular/common';
import { HostListener, Inject, Optional, ViewChild } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LayoutService, StorageService } from '@shared/services';
import { Destroyer, getNumberFromString } from '@shared/utils';
import { Device } from '@capacitor/device';
import { StatusBar, Style } from '@capacitor/status-bar';
import { takeUntil } from 'rxjs/operators';
import { Keyboard } from '@capacitor/keyboard';
import { NgProgressComponent } from 'ngx-progressbar';
import { SplashScreen } from '@capacitor/splash-screen';

@Component({
  selector: 'ic-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent extends Destroyer implements OnInit {

  @HostListener('window:resize', ['$event']) onResize(event) {
    this.setCssVariables();
  }

  get bodyElement(): HTMLElement {
    return this.document.body;
  }

  @ViewChild(NgProgressComponent) progressBar: NgProgressComponent;

  constructor(
    private translate: TranslateService,
    private storage: StorageService,
    private layout: LayoutService,
    @Optional() @Inject(DOCUMENT) private document: Document,
  ) {
    super();
    this.init();
    this.initOnMobile();
    this.setCssVariables();
  }

  ngOnInit() {
    this.layout.width$
      .pipe(takeUntil(this.destroy$))
      .subscribe(size => {
        this.bodyElement.setAttribute('screen-size', size);
      });

    setTimeout(() => {
      SplashScreen.hide({ fadeOutDuration: 600 });
    }, 3000);
  }

  private async init() {
    await this.storage.init();
    let lang = await this.storage.getItem('lang');

    if (!lang) {
      let langKey = 'en';
      try {
        const { value } = await Device.getLanguageCode();
        const nav = (value || navigator.language || '').toLowerCase().split('-');
        if (['pl', 'en'].includes(nav[0])) {
          langKey = nav[0];
        }
      } catch (e) {}
      lang = langKey;
      await this.storage.setItem('lang', langKey);
    }

    this.translate.setDefaultLang(lang);
    this.translate.use(lang);
  }

  private async setCssVariables() {
    const height = this.document.documentElement.clientHeight

    const variables: Object = {
      'mobile-top-bar-height': '57px',
      'mobile-nav-menu-height': '76px',
      'mobile-player-bar-height': '63px',
      'player-bar-height': '60px',
      'mobile-padding-top': '0px',
      'vh': `${ height }px`
    };

    const { platform, name } = await Device.getInfo();

    switch (platform) {
      case 'ios':
        const devNum = getNumberFromString(name.replace('X', '10'));
        if (devNum < 9) {
          variables[`mobile-top-bar-height`] = `56px`;
          variables[`mobile-nav-menu-height`] = `72px`;
          variables[`mobile-padding-top`] = `6px`;
        } else {
          variables[`mobile-top-bar-height`] = `80px`;
          variables[`mobile-nav-menu-height`] = `90px`;
          variables[`mobile-padding-top`] = `24px`;
        }
      break;
      case 'android':
        variables[`mobile-top-bar-height`] = `57px`;
        variables[`mobile-nav-menu-height`] = `76px`;
        variables[`mobile-padding-top`] = `6px`;
      break;
    }

    for (const prop of Object.entries(variables)) {
      this.document.documentElement.style.setProperty(`--${prop[0]}`, `${ prop[1] }`)
    }
  }

  private async initOnMobile() {
    const { platform } = await Device.getInfo();

    if (this.layout.isMobile) {
      this.progressBar.color = '#9fb9b4';
      this.progressBar.fixed = false;
      this.progressBar.spinner = true;
    }

    if (platform === 'web') { return; }

    switch (platform) {
      case 'ios':
        // window.addEventListener('statusTap', function () {
        //   console.log('statusbar tapped');
        // });
      break;
      case 'android':
        await StatusBar.setOverlaysWebView({ overlay: false });
      break;
    }

    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.hide();

    Keyboard.addListener('keyboardWillShow', info => {
      this.bodyElement.setAttribute('keyboard', 'on');
    });
    
    Keyboard.addListener('keyboardDidHide', () => {
      this.bodyElement.setAttribute('keyboard', 'off');
    });

    Keyboard.addListener('keyboardDidShow', info => {
      this.document.documentElement.style.setProperty(`--keyboard-height`, `${ info.keyboardHeight }px`);
    });
    
    Keyboard.addListener('keyboardWillHide', () => {
      this.document.documentElement.style.removeProperty(`--keyboard-height`);
    });

  }

}
